/* eslint-disable no-nested-ternary */
import * as React from 'react';
import styled, { css } from 'styled-components';

import { LazyImage } from '@crehana/ui';
import { PlayVideo, Download } from '@crehana/icons';
import { date } from '@crehana/utils';
import { useAmplitude, getSourceEventParams } from '@crehana/analytics';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import {
  trackUserActivity,
  UserActivityEvent,
} from 'Jsx/Analytics/UserActivity';
import { getResourceItemType } from 'Jsx/ResourceDetail/helpers';

interface ResourceCardProps {
  originalId: number;
  coverUrl: string;
  title: string;
  duration: number;
  detailUrl: string;
  downloads: string;
  resourceDetail?: boolean;
  resourceCategory: {
    name: string;
    slug: string;
  };
  category: string;
  currentCategory: string[];
  downloadUrl: string;
  setCurrentCategory: (category: string[]) => void;
}

const StyledImage = styled(LazyImage)`
  width: 100% !important;
  padding-bottom: 52%;
  margin-bottom: 8px;
`;

const ResourceCard = ({
  originalId,
  coverUrl,
  title,
  resourceCategory,
  duration,
  detailUrl,
  setCurrentCategory,
  category,
  currentCategory,
  downloads,
  resourceDetail,
  downloadUrl,
}: ResourceCardProps) => {
  const { logEvent } = useAmplitude();
  const { me, loading: loadingMe } = useMe();

  const downloadResource = () => {
    const itemType = getResourceItemType(category);

    if (me) {
      if (itemType === 'ebook' || itemType === 'tool') {
        trackUserActivity({
          event: UserActivityEvent.DOWNLOAD_RESOURCE,
          user: {
            segment: me.segmentationKey!,
            username: me.username!,
          },
          data: {
            product_id: originalId,
            product_name: title,
            item_type: itemType,
          },
        });
      }

      logEvent({
        // @ts-ignore
        event: 'Download Resource',
        data: {
          ...getSourceEventParams(),
          'Resource name': title,
          source: 'Resources List Landing',
          sourceDetail: 'Resource card',
        },
      });
      window.open(`${downloadUrl}?platform=direct`);
    } else {
      window.showRegisterModal(
        category !== 'tutoriales'
          ? `${window.location.href}?download=true`
          : undefined,
        undefined,
        undefined,
        'Resources - Download Button',
      );
    }
  };

  return (
    <div className="flex flex-col mb-56">
      <a href={detailUrl}>
        <StyledImage
          imgxParams={{ w: 418, h: 220, q: 70 }}
          src={coverUrl}
          alt=""
        />
      </a>
      <div
        className="description flex flex-col"
        css={`
          width: 90%;
          margin: 0 auto;
          @media (min-width: 768px) {
            width: 100%;
            margin: unset;
          }
        `}
      >
        <a
          href={detailUrl}
          className="font-h4 text-base-main hover:text-secondary-main min-h-56"
        >
          {title}
        </a>
        <div className="mt-4 flex items-center">
          {category === 'tutoriales' && duration ? (
            <span className="block mr-8 text-gray-main">
              <PlayVideo size={16} className="fill-current" ignoreColor />
            </span>
          ) : null}
          {resourceCategory && (
            <button
              type="button"
              className="font-body3 text-secondary-main relative mr-24"
              onClick={() => {
                if (
                  !resourceDetail &&
                  !currentCategory.includes(resourceCategory.slug)
                ) {
                  setCurrentCategory([
                    ...currentCategory,
                    resourceCategory.slug,
                  ]);
                }
                if (resourceDetail) {
                  window.location.href = `/recursos/${category}/?categoria=${resourceCategory.slug}`;
                }
              }}
              css={
                !!duration || !!downloads
                  ? css`
                      &:after {
                        content: '';
                        position: absolute;
                        height: 1px;
                        width: 14px;
                        background: ${props => props.theme.colors.base.main};
                        right: -20px;
                        bottom: 10px;
                      }
                    `
                  : undefined
              }
            >
              {resourceCategory.name}
            </button>
          )}

          {duration ? (
            <span className="font-body3">{`${date.toMinutes(duration)}`}</span>
          ) : downloads ? (
            <span className="font-body3">{`${downloads} Descargas`}</span>
          ) : null}

          {Boolean(downloads) && !loadingMe ? (
            <button
              type="button"
              className="h-16 ml-auto inline-block text-secondary-main hover:text-secondary-light"
              onClick={() => downloadResource()}
            >
              <Download size={16} className="fill-current" ignoreColor />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
